import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_OBJ } from '@dadohr/shared/domainTypes/emptyConstants';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { keyBy } from 'lodash';
import { useMemo } from 'react';
import { useSort } from '../../hooks/useSort';
import { ItemSorter, ROW_HEIGHTS, RowsRenderer } from '../GroupableList';
import { D, O, UserTableColumn, UserTableItemSorters } from './columns';

const ROW_TO_KEY = (d: D) => d.id;

// TODO compute available sorters!

export const UserTable = ({
  ds,
  columns,
  sorters,
  dateFormat,
  rowToHref,
  onRowClick,
  variant = 'contained',
  additionalUsers,
  headProps = {},
  highlightedFields,
  sortKey,
  sortDirection: sortDirectionProp,
  onSortChange,
  onColumnReorder,
  horizontalScroll
}: {
  ds: Doc<IUser>[];
  columns: UserTableColumn[];
  sorters: UserTableItemSorters;
  dateFormat: string;
  rowToHref?: (d: Doc<IUser>) => string;
  onRowClick?: (d: Doc<IUser>) => void;
  variant?: 'contained' | 'plain';
  headProps?: {
    sticky?: boolean;
    offset?: number;
  };

  // use to pass in other users for manager lookups.
  // example use case: you want to import users and preview them.
  // managers might be in the set of new users
  //
  // this doesn't handle the case where an import would remove
  // users. probably much rarer as we just deactivate - lets not bother for now
  additionalUsers?: Doc<IUser>[];
  highlightedFields?: Record<string, Set<string>>;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  onSortChange?: (sortKey: string, sortDirection: 'asc' | 'desc') => void;
  onColumnReorder?: (nextColumns: UserTableColumn[]) => void;
  horizontalScroll?: boolean;
}) => {
  const internal = useSort<ItemSorter<D, O>>(
    sorters.name,
    sorters.name?.items.dir
  );
  const controlled =
    sortKey !== undefined
      ? { sortBy: sorters[sortKey] ?? sorters.name, direction: sortDirectionProp ?? 'asc' }
      : null;
  const { sortBy, direction, setSort: internalSetSort } = controlled
    ? { ...internal, sortBy: controlled.sortBy, direction: controlled.direction }
    : internal;
  const setSort = onSortChange
    ? (sorter: ItemSorter<D, O>, dir: 'asc' | 'desc') => {
        onSortChange(sorter.key, dir);
      }
    : internalSetSort;
  const otherProps: O = useMemo(() => {
    return {
      dateFormat,
      additionalUsers: additionalUsers
        ? keyBy(additionalUsers, (a) => a.id)
        : EMPTY_OBJ,
      highlightedFields
    };
  }, [additionalUsers, dateFormat, highlightedFields]);

  return (
    <RowsRenderer
      variant={variant}
      columns={columns}
      rows={ds}
      rowToKey={ROW_TO_KEY}
      rowToHref={rowToHref}
      onRowClick={onRowClick}
      sorter={sortBy}
      sortDirection={direction}
      renderHead={true}
      headProps={headProps}
      onHeadClick={(c, dir) => {
        const sorter = sorters[c.key];
        if (!sorter) {
          return;
        }
        setSort(sorter, dir || sorter.items.dir);
      }}
      chunkSize={30}
      rootMargin="300px"
      rowHeight={ROW_HEIGHTS.dense}
      otherProps={otherProps}
      onColumnReorder={onColumnReorder}
      horizontalScroll={horizontalScroll}
    />
  );
};
